import { FunctionComponent } from "react";
import { useNavigate } from "react-router-dom";

import {
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  Link,
  SelectionMode,
  Stack,
  Text
} from "@bps/fluent-ui";
import { CopyableGuid } from "@components/CopyableText";
import { BpIdUserDto } from "@libs/api/gateways/bp-id/bp-id.dtos";

import { UnlinkTenantButton } from "./UnlinkTenantButton";

type SignInIdentity = BpIdUserDto["signInIdentities"][number];

interface FederatedIdentitiesTableProps {
  bpIdUserId: string;
  identities: SignInIdentity[];
  isLastFederatedTenant: boolean;
  canUnlink: boolean;
}

export const FederatedIdentitiesTable: FunctionComponent<FederatedIdentitiesTableProps> = ({
  bpIdUserId,
  identities,
  isLastFederatedTenant,
  canUnlink
}) => {
  const navigate = useNavigate();

  const columns: IColumn[] = [
    {
      key: "tenant",
      name: "Tenant",
      minWidth: 160,
      maxWidth: 280,
      isMultiline: true,
      onRender: (i: SignInIdentity) =>
        i.tenantId ? (
          <Link onClick={() => navigate(`/tenants/${i.tenantId}/plt-details`)}>
            {i.tenantName ?? i.tenantId}
          </Link>
        ) : (
          <Text>{i.tenantName ?? "Unknown tenant"}</Text>
        )
    },
    {
      key: "tenantUser",
      name: "Tenant user",
      minWidth: 160,
      maxWidth: 280,
      isMultiline: true,
      onRender: (i: SignInIdentity) =>
        i.tenantId && i.tenantUserId ? (
          <Link
            onClick={() =>
              navigate(`/tenants/${i.tenantId}/plt-users/${i.tenantUserId}`)
            }
          >
            {i.tenantUsername ?? i.tenantUserId}
          </Link>
        ) : (
          <Text>{i.tenantUsername ?? "-"}</Text>
        )
    },
    {
      key: "tenantId",
      name: "Tenant ID",
      minWidth: 120,
      maxWidth: 170,
      onRender: (i: SignInIdentity) =>
        i.tenantId ? <CopyableGuid short value={i.tenantId} /> : <Text>-</Text>
    },
    {
      key: "tenantUserId",
      name: "Tenant user ID",
      minWidth: 120,
      maxWidth: 170,
      onRender: (i: SignInIdentity) =>
        i.tenantUserId ? (
          <CopyableGuid short value={i.tenantUserId} />
        ) : (
          <Text>-</Text>
        )
    }
  ];

  if (canUnlink) {
    columns.push({
      key: "actions",
      name: "",
      minWidth: 90,
      maxWidth: 110,
      onRender: (i: SignInIdentity) =>
        i.tenantId && i.tenantUserId ? (
          <UnlinkTenantButton
            bpIdUserId={bpIdUserId}
            tenantId={i.tenantId}
            tenantUserId={i.tenantUserId}
            tenantName={i.tenantName}
            isLastFederatedTenant={isLastFederatedTenant}
          />
        ) : null
    });
  }

  return (
    <Stack styles={{ root: { maxWidth: 1200 } }}>
      <DetailsList
        items={identities}
        columns={columns}
        selectionMode={SelectionMode.none}
        layoutMode={DetailsListLayoutMode.justified}
        getKey={(i: SignInIdentity) => i.issuerAssignedId ?? ""}
      />
    </Stack>
  );
};
